import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

/**
 * Volver al listado **conservando los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir una ficha se los lleva en la query. Al volver hay que devolvérselos, y la razón es
 * que «Volver» empuja una entrada **nueva**: el historial no ayuda, porque la entrada
 * anterior, la que tenía los filtros, deja de ser la que se está usando.
 *
 * Por eso no vale `router.back()`: si la ficha se abrió desde un enlace pegado, no hay
 * entrada anterior dentro de la aplicación y `back` se sale de ella.
 *
 * ## Por qué un composable y no la misma línea en cada vista
 *
 * Porque la regla se olvida en cuanto aparece una cuarta vista, y forgetible es
 * exactamente como empezó: el listado perdía los filtros, y la mitad del arreglo —el botón
 * que limpia— estaba en la vista equivocada.
 */
export function useVolverAlListado(nombreListado: string): {
  destino: ComputedRef<RouteLocationRaw>
  volver: () => void
} {
  const route = useRoute()
  const router = useRouter()

  const destino = computed<RouteLocationRaw>(() => ({
    name: nombreListado,
    // Se devuelve tal cual, sin filtrar. La query de una ficha es la del listado: si
    // mañana se le añade algo propio, quitarlo aquí rompería ese algo sin avisar.
    query: route.query,
  }))

  return {
    destino,
    volver: () => {
      void router.push(destino.value)
    },
  }
}